import React, { useState, useRef, useEffect, useCallback, createContext, useContext } from "react";
import { ChevronDown } from "lucide-react";

/**
 * Dropdown – a flexible, composable dropdown menu component.
 *
 * ── Basic usage ────────────────────────────────────────────────────────────
 *
 *   <Dropdown>
 *     <Dropdown.Trigger>Options</Dropdown.Trigger>
 *     <Dropdown.Menu>
 *       <Dropdown.Item onClick={() => console.log("edit")}>Edit</Dropdown.Item>
 *       <Dropdown.Item onClick={() => console.log("delete")} variant="danger">Delete</Dropdown.Item>
 *     </Dropdown.Menu>
 *   </Dropdown>
 *
 * ── With icons ─────────────────────────────────────────────────────────────
 *
 *   import { Edit2, Trash2 } from "lucide-react";
 *
 *   <Dropdown>
 *     <Dropdown.Trigger>Actions</Dropdown.Trigger>
 *     <Dropdown.Menu>
 *       <Dropdown.Item icon={<Edit2 className="w-4 h-4" />}>Edit</Dropdown.Item>
 *       <Dropdown.Divider />
 *       <Dropdown.Item icon={<Trash2 className="w-4 h-4" />} variant="danger">Delete</Dropdown.Item>
 *     </Dropdown.Menu>
 *   </Dropdown>
 *
 * ── Custom trigger ─────────────────────────────────────────────────────────
 *
 *   <Dropdown>
 *     <Dropdown.Trigger asChild>
 *       <button className="p-2 rounded-full hover:bg-gray-100">
 *         <MoreHorizontal className="w-4 h-4" />
 *       </button>
 *     </Dropdown.Trigger>
 *     <Dropdown.Menu align="right">
 *       <Dropdown.Item>View</Dropdown.Item>
 *     </Dropdown.Menu>
 *   </Dropdown>
 *
 * ── Props ──────────────────────────────────────────────────────────────────
 *
 *  <Dropdown>
 *    open?       {bool}    – controlled open state
 *    onOpenChange{fn}      – callback when open state changes
 *
 *  <Dropdown.Trigger>
 *    asChild?    {bool}    – render children as trigger (no wrapper button)
 *    showArrow?  {bool}    – show chevron arrow, default true
 *    className?  {string}
 *
 *  <Dropdown.Menu>
 *    align?      {"left"|"right"}  – menu alignment, default "left"
 *    width?      {string}          – tailwind w-* class, default "w-48"
 *    className?  {string}
 *
 *  <Dropdown.Item>
 *    icon?       {ReactNode}
 *    variant?    {"default"|"danger"|"success"}
 *    disabled?   {bool}
 *    className?  {string}
 *    onClick?    {fn}
 *
 *  <Dropdown.Label>  – non-interactive section label
 *  <Dropdown.Divider> – horizontal separator
 */

// ── Context ───────────────────────────────────────────────────────────────────
const DropdownContext = createContext(null);
const useDropdown = () => useContext(DropdownContext);

// ── Root ──────────────────────────────────────────────────────────────────────
const Dropdown = ({ children, open: controlledOpen, onOpenChange }) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const ref = useRef(null);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const setOpen = useCallback((val) => {
    if (!isControlled) setInternalOpen(val);
    onOpenChange?.(val);
  }, [isControlled, onOpenChange]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [setOpen]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [setOpen]);

  return (
    <DropdownContext.Provider value={{ open, setOpen }}>
      <div className="relative w-full" ref={ref}>
        {children}
      </div>
    </DropdownContext.Provider>
  );
};

// ── Trigger ───────────────────────────────────────────────────────────────────
const Trigger = ({ children, asChild = false, showArrow = true, className = "" }) => {
  const { open, setOpen } = useDropdown();

  if (asChild) {
    return React.cloneElement(React.Children.only(children), {
      onClick: (e) => {
        children.props.onClick?.(e);
        setOpen(!open);
      },
      "aria-expanded": open,
      "aria-haspopup": true,
    });
  }

  return (
    <button
      type="button"
      onClick={() => setOpen(!open)}
      aria-expanded={open}
      aria-haspopup="true"
      className={`inline-flex items-center gap-2 w-full px-4 py-2.5 rounded-lg border border-surface-border bg-white text-sm font-medium text-brand-text hover:bg-surface transition-colors ${className}`}
    >
      {children}
      {showArrow && (
        <ChevronDown
          className={`w-4 h-4 text-brand-muted transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      )}
    </button>
  );
};

// ── Menu ──────────────────────────────────────────────────────────────────────
const Menu = ({ children, align = "left", width = "w-48", className = "" }) => {
  const { open } = useDropdown();

  const alignClass = align === "right" ? "right-0" : "left-0";

  if (!open) return null;

  return (
    <div
      className={`absolute ${alignClass} mt-1.5 ${width} bg-white border border-surface-border rounded-xl shadow-lg z-50 py-1 overflow-hidden animate-fade-in ${className}`}
      role="menu"
    >
      {children}
    </div>
  );
};

// ── Item ──────────────────────────────────────────────────────────────────────
const itemVariants = {
  default: "text-brand-text hover:bg-surface hover:text-primary",
  danger:  "text-danger   hover:bg-red-50  hover:text-danger",
  success: "text-success  hover:bg-green-50 hover:text-success",
};

const Item = ({
  children,
  icon,
  variant = "default",
  disabled = false,
  className = "",
  onClick,
}) => {
  const { setOpen } = useDropdown();

  const handleClick = () => {
    if (disabled) return;
    onClick?.();
    setOpen(false);
  };

  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={handleClick}
      className={`flex items-center gap-2.5 w-full px-4 py-2.5 text-sm transition-colors text-left
        ${itemVariants[variant]}
        ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}
        ${className}`}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span>{children}</span>
    </button>
  );
};

// ── Label ─────────────────────────────────────────────────────────────────────
const Label = ({ children, className = "" }) => (
  <div className={`px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-brand-light ${className}`}>
    {children}
  </div>
);

// ── Divider ───────────────────────────────────────────────────────────────────
const Divider = () => <div className="my-1 border-t border-surface-border" />;

// ── Compose ───────────────────────────────────────────────────────────────────
Dropdown.Trigger = Trigger;
Dropdown.Menu    = Menu;
Dropdown.Item    = Item;
Dropdown.Label   = Label;
Dropdown.Divider = Divider;

export default Dropdown;
